/**
 * Hook for real-time synchronization of matches and chats
 */

import { matchesService } from "@features/messaging/services/matchesService";
import type { Chat, User } from "@fitties/shared";
import { backendRegistry } from "@infrastructure/providers/backendRegistry";
import logger from "@logger";

import { matchesQueryKeys } from "@shared/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { where } from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

const REALTIME_DEBOUNCE_MS = 150;

const SUBSCRIPTION_ERROR_MESSAGE = "Live updates paused.";

export interface UseMatchesRealtimeReturn {
  error: string | null;
  retry: () => void;
}

/**
 * Hook for real-time synchronization of matches
 * Subscribes to user document and the caller's chats (array-contains) for live updates.
 */
export function useMatchesRealtime(userId: string): UseMatchesRealtimeReturn {
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const [error, setError] = useState<string | null>(null);

  const subscribe = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (!userId) {
      return;
    }

    setError(null);
    logger.info("Setting up real-time subscriptions for matches", { userId });

    const scheduleRefresh = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(async () => {
        debounceRef.current = null;
        try {
          const allMatches = await matchesService.getAllMatches(userId);
          queryClient.setQueryData(matchesQueryKeys.allMatches(userId), allMatches);
          logger.info("Real-time matches updated", {
            userId,
            allMatchesCount: allMatches.length,
          });
        } catch (refreshError) {
          logger.error("Error updating matches from real-time subscription:", refreshError);
        }
      }, REALTIME_DEBOUNCE_MS);
    };

    const handleSubscriptionError = (subscriptionError: Error, source: string) => {
      logger.error("Matches realtime subscription failed", {
        userId,
        source,
        code: "code" in subscriptionError ? subscriptionError.code : undefined,
        message: subscriptionError.message,
      });
      setError(SUBSCRIPTION_ERROR_MESSAGE);
    };

    const unsubscribeUser = backendRegistry.database.subscribeToDocument<User>(
      "users",
      userId,
      (userData) => {
        if (!userData) {
          logger.warn("User data not found for real-time subscription", { userId });
          return;
        }
        scheduleRefresh();
      },
      (subscriptionError) => handleSubscriptionError(subscriptionError, "users")
    );

    const unsubscribeChats = backendRegistry.database.subscribeToCollection<Chat>(
      "chats",
      (chats) => {
        if (chats.length > 0) {
          logger.info("Real-time chat updates detected", {
            userId,
            chatCount: chats.length,
          });
          scheduleRefresh();
        }
      },
      [where("participant_uids", "array-contains", userId)],
      (subscriptionError) => handleSubscriptionError(subscriptionError, "chats")
    );

    unsubscribeRef.current = () => {
      logger.info("Cleaning up real-time subscriptions for matches", { userId });
      unsubscribeUser();
      unsubscribeChats();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [userId, queryClient]);

  useEffect(() => {
    subscribe();
    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [subscribe]);

  return { error, retry: subscribe };
}
